import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import images from "../../images";
import { useLoginMutation, useGetUserInfoQuery } from "../../services/apiSlice";
import { toast } from "react-toastify";
import { FormFieldSigIn } from "../../components/common/Input/InputSignIn";
import LoadingCommon from "../../components/common/LoadingStatus/LoadingCommon";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

export default function SignInPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect_to");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [login, { isLoading: loginLoading }] = useLoginMutation();
  const [token, setToken] = useState(sessionStorage.getItem("accessToken"));
  const [isRedirectLoading, setIsRedirectLoading] = useState(false);

  const {
    data: userInfo,
    isSuccess,
    isLoading,
  } = useGetUserInfoQuery(null, {
    skip: !token,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleContinue = async () => {
    const body = {
      email: formData.email,
      password: formData.password,
    };

    try {
      const response = await login(body).unwrap();

      if (response.data.access_token && response.data.refresh_token) {
        sessionStorage.setItem("accessToken", response.data.access_token);
        sessionStorage.setItem("refreshToken", response.data.refresh_token);

        setToken(response.data.access_token);
        toast.success(t("signIn.signInSuccess"));
      }
    } catch (err) {
      toast.error(err?.data?.detail[0].msg || t("signIn.signInFailed"));
    }
  };

  useEffect(() => {
    if (isSuccess && userInfo) {
      setIsRedirectLoading(true);
      setTimeout(() => {
        setIsRedirectLoading(false);
        if (redirectTo) {
          navigate(redirectTo);
        } else if (userInfo.data.is_first_login === true) {
          navigate("/");
        } else {
          navigate("/search-company");
        }
      }, 0);
    }
  }, [isSuccess, userInfo, navigate, redirectTo]);

  const handleEyes = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  return (
    <main className="mx-auto w-full bg-yellow-50 max-w-[480px]">
      <LoadingCommon statusLoading={isRedirectLoading} />
      <Helmet>
        <meta name="theme-color" content="#fefce8" />
      </Helmet>
      <section className="flex justify-center items-center w-full h-[300px]">
        <img src={images.LogoLogin} className="w-[180px]" alt="Logo" />
      </section>
      <section className="flex flex-col justify-between px-4 py-6 w-full bg-white min-h-[calc(100dvh-200px)] rounded-t-[20px] shadow-[0px_0px_4px_rgba(0,0,0,0.08)]">
        <div className="flex flex-col items-center justify-center w-full max-w-[340px] mx-auto">
          <section className="w-full text-center">
            <div className="flex flex-col items-center">
              <h1 className="text-4xl font-bold text-gray-800">
                {" "}
                {t("signIn.title")}
              </h1>
              <div className="mt-3">
                <span className="inline-block px-3 py-1.5 text-xs text-amber-400 bg-yellow-50 rounded-xl">
                  {t("signIn.businessAccount")}
                </span>
              </div>
            </div>
          </section>

          <div className="flex flex-col w-full text-sm text-neutral-500">
            <FormFieldSigIn
              placeholder={t("signIn.email")}
              icon={images.LetterIcon}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              disabled={loginLoading || isRedirectLoading}
            />

            <FormFieldSigIn
              placeholder={t("signIn.password")}
              icon={images.KeyIcon}
              type={isPasswordVisible ? "text" : "password"}
              handleRightIcon={handleEyes}
              rightIcon={images.EyesIcon}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              disabled={loginLoading || isRedirectLoading}
            />
            <p className="mt-2 text-right">
              <span
                onClick={() => navigate("/forgot-password")}
                style={{
                  fontFamily:
                    "Figtree, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: 700,
                  color: "rgba(63,84,223,1)",
                  cursor: "pointer",
                  pointerEvents: isRedirectLoading ? "none" : "auto",
                }}
              >
                {t("signIn.forgotPassword")}
              </span>
            </p>
            <button
              onClick={handleContinue}
              className={`w-full px-4 py-3 mt-2 text-lg font-semibold text-white rounded-xl ${
                loginLoading || isLoading
                  ? "bg-indigo-400 cursor-not-allowed"
                  : "bg-[#3F54DF] hover:bg-indigo-700"
              }`}
              disabled={loginLoading || isLoading}
            >
              {loginLoading || isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8h8a8 8 0 01-16 0z"
                    />
                  </svg>
                  {t("base.loading")}
                </span>
              ) : (
                t("base.continue")
              )}
            </button>
            <div className="mt-5 w-full text-center text-sm">
              <p>
                <span
                  style={{
                    fontFamily:
                      "Figtree, -apple-system, Roboto, Helvetica, sans-serif",
                    fontWeight: 400,
                    color: "rgba(120,122,122,1)",
                  }}
                >
                  {t("signIn.noAccount")}
                </span>
                <span
                  onClick={() => navigate("/register")}
                  style={{
                    fontFamily:
                      "Figtree, -apple-system, Roboto, Helvetica, sans-serif",
                    fontWeight: 700,
                    color: "rgba(63,84,223,1)",
                    cursor: "pointer",
                    pointerEvents: isRedirectLoading ? "none" : "auto",
                  }}
                >
                  {t("signIn.signUp")}
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
